import React, { useMemo } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line
} from 'recharts';
import { Card } from '@vehix/portal/components/ui/card';
import { TrendingUp } from 'lucide-react';
import type { VolumeTrendPoint } from '@vehix/shared/types/recallSnapshots';

interface Props {
  points: VolumeTrendPoint[];
}

const OEM_COLORS: Record<string, string> = {
  Ford: '#2563eb',
  Stellantis: '#dc2626',
  Kia: '#059669',
  Hyundai: '#7c3aed',
  'General Motors': '#d97706'
};
const FALLBACK_COLOR = '#6b7280';

interface WideRow {
  date: string;
  [key: string]: string | number | null | boolean;
}

/**
 * Custom dot renderer: emphasizes outliers with larger red marker.
 */
function renderDot(props: {
  cx?: number;
  cy?: number;
  payload?: WideRow;
  dataKey?: string;
}): React.ReactElement {
  const { cx, cy, payload, dataKey } = props;
  if (cx == null || cy == null || !payload || !dataKey) return <></>;
  const isOutlier = payload[`${dataKey}__outlier`] === true;
  if (isOutlier) {
    return (
      <circle cx={cx} cy={cy} r={5} fill='#dc2626' stroke='#fff' strokeWidth={1.5} />
    );
  }
  return <circle cx={cx} cy={cy} r={2.5} fill={String(payload[`${dataKey}__color`] ?? FALLBACK_COLOR)} />;
}

export const VolumeTrendChart: React.FC<Props> = ({ points }) => {
  const { rows, oems } = useMemo(() => {
    const dateMap = new Map<string, WideRow>();
    const oemSet = new Set<string>();
    for (const p of points) {
      oemSet.add(p.oem);
      const row = dateMap.get(p.execution_date) ?? { date: p.execution_date };
      row[p.oem] = p.total_recalls;
      row[`${p.oem}__outlier`] = p.is_outlier;
      row[`${p.oem}__color`] = OEM_COLORS[p.oem] ?? FALLBACK_COLOR;
      row[`${p.oem}__baseline`] = p.baseline_mean;
      dateMap.set(p.execution_date, row);
    }
    const rows = [...dateMap.values()].sort((a, b) => String(a.date).localeCompare(String(b.date)));
    return { rows, oems: [...oemSet].sort() };
  }, [points]);

  const outlierCount = points.filter(p => p.is_outlier).length;

  return (
    <Card className='p-4'>
      <div className='flex items-baseline justify-between gap-2 mb-3'>
        <div className='flex items-center gap-2'>
          <TrendingUp className='h-4 w-4 text-primary' />
          <h3 className='text-sm font-semibold'>Volume trend (per OEM)</h3>
        </div>
        <span className='text-[11px] text-muted-foreground'>
          rolling-4 baseline · {outlierCount} outlier{outlierCount === 1 ? '' : 's'}
        </span>
      </div>

      {rows.length === 0 ? (
        <p className='text-xs text-muted-foreground py-6 text-center'>
          No execution data in window.
        </p>
      ) : (
        <div className='h-64'>
          <ResponsiveContainer width='100%' height='100%'>
            <ComposedChart data={rows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray='3 3' className='stroke-muted' />
              <XAxis
                dataKey='date'
                tick={{ fontSize: 11 }}
                tickFormatter={d =>
                  new Date(String(d)).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                }
              />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{ fontSize: 12 }}
                formatter={(value: number, name: string, props: { payload?: WideRow }) => {
                  const out = props.payload?.[`${name}__outlier`];
                  return [
                    `${value?.toLocaleString?.() ?? value}${out ? ' ⚠ outlier' : ''}`,
                    name
                  ];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {oems.map(oem => (
                <Line
                  key={oem}
                  type='monotone'
                  dataKey={oem}
                  stroke={OEM_COLORS[oem] ?? FALLBACK_COLOR}
                  strokeWidth={2}
                  dot={renderDot as never}
                  activeDot={{ r: 5 }}
                  connectNulls
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
};

export default VolumeTrendChart;
